<script lang="ts" setup>
	const isOpen = defineModel<boolean>({
		default: false
	})
	const props = defineProps<{
		selected?: 'register' | 'login'
	}>()
	const selectedMethod = shallowRef<'register' | 'login'>('login')
	const businessOnboardingUrl = import.meta.env.VITE_BUSINESS_URL + '/register'
	const businessLoginUrl = import.meta.env.VITE_BUSINESS_URL + '/login'
	const individualOnboardingUrl = import.meta.env.VITE_INDIVIDUAL_URL + '/register'
	const individualLoginUrl = import.meta.env.VITE_INDIVIDUAL_URL + '/login'
	const { sourceChannel } = useUtils()
	const authRoutes = ref([
		{
			method: 'login',
			options: [
				{
					title: 'Business account',
					description: 'Login to your business account to manage plans for your staff and business assets.',
					src: businessLoginUrl,
					icon: {
						name: 'briefcase',
						dir: 'fill'
					}
				},

				{
					title: 'Personal account',
					description: 'Login your personal account to manage plans for yourself and your loved ones.',
					src: individualLoginUrl,
					icon: {
						name: 'persons',
						dir: 'fill'
					}
				}
			]
		},
		{
			method: 'register',
			options: [
				{
					title: 'Business account',
					description: 'Create a business account to protect your staff and business assets.',
					src: businessOnboardingUrl,
					icon: {
						name: 'briefcase',
						dir: 'fill'
					}
				},

				{
					title: 'Personal account',
					description: 'Create a personal account to protect yourself and your loved ones.',
					src: individualOnboardingUrl,
					icon: {
						name: 'persons',
						dir: 'fill'
					}
				}
			]
		}
	])
	const headerCaption = computed(() => (selectedMethod.value === 'login' ? 'Log Into Account' : 'Create An Account'))
	const trackLink = (src: string) => {
		let label = selectedMethod.value === 'login' ? 'web_login' : 'web_register'

		resolve(() => {
			useMixpanel()?.track(label, {
				page_url: src
			})
		})
	}
	watch(
		() => props.selected,
		val => {
			if (val) {
				selectedMethod.value = val
			}
		},
		{ immediate: true }
	)
</script>

<template>
	<ui-modal v-model:show="isOpen">
		<div class="max-w-[680px] mx-auto w-full text-[#094163]">
			<ui-title-header
				:title="headerCaption"
				class="pt-5 border-b broder-gray-100 custom-transition-2 dark:border-gray-700"
				:ui="{ size: 'sm:text-[3em] text-[1.5em] text-center' }"
			/>
			<div class="flex flex-col">
				<div class="flex items-center justify-center flex-wrap gap-10 pt-10 w-full">
					<template v-for="route in authRoutes" :key="route.method">
						<ClientOnly>
							<template v-if="route.method === selectedMethod">
								<nuxt-link
									v-for="(link, index) in route.options"
									:key="index"
									:to="{ path: link.src, query: sourceChannel }"
									target="_blank"
									external
									class="max-w-[320px] w-full min-h-[300px] bg-inherit group custom-transition-2 dark:border-gray-700 hover:bg-base-50 hover:dark:bg-gray-800 hover:border-main border border-gray-200 rounded-[4px] p-5 flex-none"
									@click="trackLink(link.src)"
								>
									<div
										class="h-[54px] w-[54px] custom-transition-2 flex items-center justify-center group-hover:bg-white rounded-full bg-base-50"
									>
										<tools-icon
											:icon="link.icon.name"
											:dir="link.icon.dir"
											:height="24"
											:width="24"
										/>
									</div>

									<div class="mt-5 space-y-3">
										<h4 class="font-semibold sm:text-lg text-base text-blue-950 dark:text-main">
											{{ link.title }}
										</h4>
										<p class="text-sm text-gray-600 dark:text-gray-400">
											{{ link.description }}
										</p>
										<div
											class="top-8 relative custom-transition-2 animate-x opacity-0 group-hover:opacity-100"
										>
											<tools-icon
												icon="arrow-short-r"
												dir="arrows"
												path-class="fill-gray-700 dark:fill-gray-400"
											/>
										</div>
									</div>
								</nuxt-link>
							</template>
						</ClientOnly>
					</template>
				</div>

				<div class="text-center sm:text-xl font-medium pt-10 mb-20 text-gray-600 dark:text-gray-400">
					<span v-show="selectedMethod === 'register'">
						Already have an account?
						<span
							role="button"
							title="log in"
							aria-describedby="button"
							aria-label="login"
							class="text-main"
							name="login"
							@click="selectedMethod = 'login'"
						>
							Log In
						</span>
					</span>
					<span v-show="selectedMethod === 'login'">
						Don’t have an account?
						<span
							role="button"
							aria-describedby="button"
							aria-label="register"
							class="text-main"
							name="register"
							@click="selectedMethod = 'register'"
						>
							Get Covered
						</span>
					</span>
				</div>
			</div>
		</div>
	</ui-modal>
</template>

<style scoped>
	@keyframes moveX {
		0% {
			transform: translateX(0);
		}

		10%,
		30% {
			transform: translateX(-5px);
		}

		55%,
		70% {
			transform: translateX(-5px);
			transform-origin: 0 5px;
		}

		80%,
		100% {
			transform: translateX(0px);
		}
	}
	.animate-x {
		animation: moveX 1.5s linear infinite;
		transform-origin: center;
		transform-style: preserve-3d;
	}
</style>
